import { MAX_TEAM_REPORT_SUMMARY_BULLET_CHARS, MAX_TEAM_REPORT_SUMMARY_BULLETS, } from './team-report.js';
/**
 * Bản nháp tất định của phần tóm tắt **trên cùng** — APM-TWR-h, gạch đầu dòng
 * từ APM-TWR-i.
 *
 * Dùng chung cho hai bên, nên nằm cạnh schema:
 *
 * - web điền sẵn ô "Tóm tắt" của trình sửa bằng nó khi ô còn trống — đây là
 *   **đường làm tay** (`AiSkill.manual`) của `summarize_team_work`: chưa có key
 *   AI thì leader vẫn có một bản tóm tắt đúng luật để sửa;
 * - API dùng `summaryProjects` để biết dự án nào **phải** có đoạn (`finalize`
 *   của skill), và unit test canh luật bằng chính hàm này.
 *
 * ## Luật (người dùng, 2026-09-25)
 *
 * | Việc | Gạch |
 * | --- | --- |
 * | task đã xong | "hoàn thành <tiêu đề>" |
 * | task dở, có mục checklist xong trong kỳ | "<tiêu đề>: xong <mục 1>, <mục 2>" |
 * | task dở, không có checklist | "tiếp tục làm <tiêu đề>" |
 * | issue đã xử lý | "xử lý <tiêu đề>" |
 *
 * Mỗi dự án một mục, **mỗi việc một gạch** (APM-TWR-i — TWR-h nối chúng thành
 * một đoạn liền, người dùng muốn đọc từng việc). **Không con số tiến độ**:
 * phần này là thứ người ta đọc lướt hoặc dán đi, và "từ 20% lên 60%" đã có ở
 * các phần bên dưới. Tên dự án không nằm trong gạch — giao diện in đậm nó ở
 * đầu mục.
 *
 * Câu chữ là **nội dung** của báo cáo (cùng loại với lời AI viết, lưu xuống
 * cùng cột), không phải chuỗi giao diện — nên viết tiếng Việt ở đây, không qua
 * i18n.
 */
/** Trần một tiêu đề hay một mục checklist trong gạch, để một chuỗi dài bất thường không nuốt cả gạch. */
const TITLE_CHARS = 200;
const CHECKLIST_ITEM_CHARS = 150;
/** Chừa chỗ cho đuôi " và n mục khác" khi gạch checklist chạm trần. */
const CHECKLIST_TAIL_RESERVE = 20;
/**
 * Dự án có **việc** trong kỳ: ít nhất một task xong, một task đang làm, hoặc
 * một issue đã xử lý. Dự án chỉ có giờ bấm không gắn việc nào thì không có gì
 * để kể — nó không có đoạn tóm tắt.
 */
export function projectHasWork(project) {
    return project.people.some((person) => person.tasksDone.length > 0 ||
        person.tasksOngoing.length > 0 ||
        person.issuesResolved.length > 0);
}
/** Những dự án phải có một mục trong `summary`, theo thứ tự `facts.projects`. */
export function summaryProjects(facts) {
    return facts.projects.filter(projectHasWork);
}
/** Bản nháp: mỗi dự án có việc một mục, theo thứ tự `facts.projects`. */
export function draftSummary(facts) {
    return summaryProjects(facts).map(draftParagraph);
}
function clip(text, max) {
    return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}
function titleOf(item) {
    const title = item.title.trim();
    return clip(title === '' ? item.key : title, TITLE_CHARS);
}
/**
 * Một dự án → một mục, mỗi việc một gạch. Gộp theo **việc**, không theo người: một task nhiều
 * người cùng làm chỉ được kể một lần, và mục checklist của họ gộp chung. Task
 * đã xong thì chỉ là "hoàn thành", kể cả khi có người khác vẫn còn nó trong
 * `tasksOngoing` (không thể có ở ảnh chụp hiện tại, nhưng hai vế mâu thuẫn
 * nhau trong một mục là thứ không được phép in ra).
 */
function draftParagraph(project) {
    const done = new Map();
    const ongoing = new Map();
    const issues = new Map();
    for (const person of project.people) {
        for (const task of person.tasksDone) {
            if (!done.has(task.taskId))
                done.set(task.taskId, titleOf(task));
        }
        for (const task of person.tasksOngoing) {
            let entry = ongoing.get(task.taskId);
            if (!entry) {
                entry = { title: titleOf(task), items: [] };
                ongoing.set(task.taskId, entry);
            }
            for (const item of task.checklistDone ?? []) {
                const text = clip(item.text.trim(), CHECKLIST_ITEM_CHARS);
                if (text !== '' && !entry.items.includes(text))
                    entry.items.push(text);
            }
        }
        for (const issue of person.issuesResolved) {
            if (!issues.has(issue.issueId))
                issues.set(issue.issueId, titleOf(issue));
        }
    }
    for (const taskId of done.keys())
        ongoing.delete(taskId);
    const task = (id) => ({ type: 'task', id });
    const bullets = [
        ...[...done].map(([id, title]) => ({ text: `hoàn thành ${title}`, ref: task(id) })),
        ...[...ongoing]
            .filter(([, entry]) => entry.items.length > 0)
            .map(([id, entry]) => ({
            text: checklistPhrase(entry.title, entry.items),
            ref: task(id),
        })),
        ...[...ongoing]
            .filter(([, entry]) => entry.items.length === 0)
            .map(([id, entry]) => ({ text: `tiếp tục làm ${entry.title}`, ref: task(id) })),
        ...[...issues].map(([id, title]) => ({
            text: `xử lý ${title}`,
            ref: { type: 'issue', id },
        })),
    ];
    return { projectId: project.projectId, ...capBullets(bullets) };
}
/** "<tiêu đề>: xong a, b" — quá trần một gạch thì "…, và n mục khác". */
function checklistPhrase(title, items) {
    const head = `${title}: xong `;
    const cap = MAX_TEAM_REPORT_SUMMARY_BULLET_CHARS - CHECKLIST_TAIL_RESERVE;
    const kept = [];
    for (const item of items) {
        const next = [...kept, item].join(', ');
        if (kept.length > 0 && head.length + next.length > cap)
            break;
        kept.push(item);
    }
    const more = items.length - kept.length;
    return clip(`${head}${kept.join(', ')}${more > 0 ? ` và ${more} mục khác` : ''}`, MAX_TEAM_REPORT_SUMMARY_BULLET_CHARS - 1);
}
/**
 * Giữ dưới trần `MAX_TEAM_REPORT_SUMMARY_BULLETS` gạch. Chạm trần thì gạch cuối
 * **nói ra** số việc còn lại — cắt im lặng là một mục tóm tắt thiếu việc mà
 * người đọc không biết. `refs` chỉ gồm việc được kể.
 */
function capBullets(all) {
    const kept = all.length > MAX_TEAM_REPORT_SUMMARY_BULLETS
        ? all.slice(0, MAX_TEAM_REPORT_SUMMARY_BULLETS - 1)
        : all;
    const more = all.length - kept.length;
    return {
        bullets: [...kept.map((bullet) => bullet.text), ...(more > 0 ? [`và ${more} việc khác`] : [])],
        refs: kept.map((bullet) => bullet.ref),
    };
}
//# sourceMappingURL=team-report-summary.js.map